import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation } from 'wouter';
import { useProjects } from '../hooks/use-projects';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';

// UI Components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';

// Icons
import { 
  Plus, 
  Calendar as CalendarIcon,
  ArrowLeft,
  Building,
  User,
  DollarSign,
  FileText
} from 'lucide-react';

// Layout Components
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';


// Custom hook to fetch contractor services
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';

// Validation schema
const projectFormSchema = z.object({
  title: z.string().min(2, "Project title must be at least 2 characters"),
  description: z.string().optional(),
  serviceType: z.string().min(1, "Please select a service type"),
  clientId: z.number().optional(), // Make client optional
  budget: z.string().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  notes: z.string().optional(),
  status: z.string().default("pending")
});

interface ContractorService {
  id: string;
  name: string;
  serviceType: string;
  unit: string;
  laborRate: number;
  laborMethod: string;
}

type ProjectFormData = z.infer<typeof projectFormSchema>;

export default function ProjectCreatePage() {
  const [selectedServiceType, setSelectedServiceType] = useState<string>('');
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { createProject, isCreating } = useProjects();

  // Fetch contractor's custom services
  const { data: contractorServices = [], isLoading: isLoadingServices } = useQuery<ContractorService[]>({
    queryKey: ['/api/direct/services'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/direct/services');
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      title: '',
      description: '',
      serviceType: '',
      clientId: undefined,
      budget: '',
      startDate: undefined,
      endDate: undefined,
      notes: '',
      status: 'pending'
    }
  });

  // Update form when service type changes
  React.useEffect(() => {
    form.setValue('serviceType', selectedServiceType);
  }, [selectedServiceType, form]);

  const onSubmit = async (data: ProjectFormData) => {
    try {
      console.log('🔍 Form submission data:', data);
      
      const projectData = {
        ...data,
        budget: data.budget ? data.budget.toString() : undefined,
        startDate: data.startDate ? (data.startDate instanceof Date ? data.startDate.toISOString() : data.startDate) : undefined,
        endDate: data.endDate ? (data.endDate instanceof Date ? data.endDate.toISOString() : data.endDate) : undefined,
        serviceType: data.serviceType,
        clientId: data.clientId || undefined
      };

      console.log('🔍 Sending project data to API:', projectData);
      await createProject(projectData);
      
      toast({
        title: 'Project created successfully!',
        description: `Project "${data.title}" has been created and is ready for workflow tracking.`,
      });

      navigate('/projects');
    } catch (error) {
      toast({
        title: 'Error creating project',
        description: 'Failed to create project. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="p-8 space-y-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <img 
                  src="/remodra-logo.png" 
                  alt="Remodra Logo" 
                  className="h-16 w-16 object-contain"
                />
              </div>
              <h1 className="text-3xl font-bold text-green-500 mb-2">Create New Project</h1>
              <p className="text-slate-500 dark:text-slate-400">Set up a new construction project with workflow tracking</p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 gap-8">
                {/* Project Form */}
                <div>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-green-500 flex items-center">
                        <Plus className="h-5 w-5 mr-2" />
                        Project Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                          {/* Basic Information */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold flex items-center">
                              <Building className="h-4 w-4 mr-2" />
                              Basic Information
                            </h3>
                            
                            <FormField
                              control={form.control}
                              name="title"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="font-medium">Project Title</FormLabel>
                                  <FormControl>
                                    <Input 
                                      placeholder="Enter project title" 
                                      {...field} 
                                      className="remodra-input"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="font-medium">Description</FormLabel>
                                  <FormControl>
                                    <Textarea 
                                      placeholder="Describe the project scope and requirements" 
                                      {...field} 
                                      className="remodra-input"
                                      rows={3}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="serviceType"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Service Type</FormLabel>
                                  <Select 
                                    value={selectedServiceType} 
                                    onValueChange={(value) => {
                                      setSelectedServiceType(value);
                                      field.onChange(value);
                                    }}
                                  >
                                    <FormControl>
                                      <SelectTrigger className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-600">
                                        <SelectValue placeholder="Select service type" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {isLoadingServices ? (
                                        <SelectItem value="loading-services" disabled>Loading services...</SelectItem>
                                      ) : contractorServices.length === 0 ? (
                                        <SelectItem value="no-services" disabled>No services available</SelectItem>
                                      ) : (
                                        contractorServices.map((service) => {
                                          const serviceType = service.serviceType || (service as any).service_type || '';
                                          const serviceName = service.name || '';
                                          return (
                                            <SelectItem key={service.id || serviceType} value={serviceType}>
                                              {serviceName} {serviceType ? `(${serviceType})` : ''}
                                            </SelectItem>
                                          );
                                        })
                                      )}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Financial Information */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold flex items-center">
                              <DollarSign className="h-4 w-4 mr-2" />
                              Financial Information
                            </h3>
                            
                            <FormField
                              control={form.control}
                              name="budget"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Budget</FormLabel>
                                  <FormControl>
                                    <Input 
                                      placeholder="Enter project budget" 
                                      type="number"
                                      step="0.01"
                                      {...field} 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Timeline */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-2" />
                              Timeline
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={form.control}
                                name="startDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Start Date</FormLabel>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <FormControl>
                                          <Button
                                            variant="outline"
                                            className="w-full"
                                          >
                                            {field.value ? (
                                              format(field.value, "PPP")
                                            ) : (
                                              <span>Pick a start date</span>
                                            )}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                          </Button>
                                        </FormControl>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                          mode="single"
                                          selected={field.value}
                                          onSelect={field.onChange}
                                          disabled={(date) =>
                                            date < new Date()
                                          }
                                          initialFocus
                                        />
                                      </PopoverContent>
                                    </Popover>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="endDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>End Date</FormLabel>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <FormControl>
                                          <Button
                                            variant="outline"
                                            className="w-full"
                                          >
                                            {field.value ? (
                                              format(field.value, "PPP")
                                            ) : (
                                              <span>Pick an end date</span>
                                            )}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                          </Button>
                                        </FormControl>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                          mode="single"
                                          selected={field.value}
                                          onSelect={field.onChange}
                                          disabled={(date) =>
                                            date < new Date()
                                          }
                                          initialFocus
                                        />
                                      </PopoverContent>
                                    </Popover>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>

                          {/* Additional Notes */}
                          <div className="space-y-4">
                            <h3 className="text-lg font-semibold flex items-center">
                              <FileText className="h-4 w-4 mr-2" />
                              Additional Notes
                            </h3>
                            
                            <FormField
                              control={form.control}
                              name="notes"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Notes</FormLabel>
                                  <FormControl>
                                    <Textarea 
                                      placeholder="Add any additional notes or special requirements" 
                                      {...field} 
                                      rows={3}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Form Actions */}
                          <div className="flex items-center gap-4 pt-6">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => navigate('/projects')}
                              className="flex items-center"
                            >
                              <ArrowLeft className="h-4 w-4 mr-2" />
                              Cancel
                            </Button>
                            <Button 
                              type="submit" 
                              disabled={isCreating}
                              className="flex items-center"
                            >
                              {isCreating ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Creating...
                                </>
                              ) : (
                                <>
                                  <Plus className="h-4 w-4 mr-2" />
                                  Create Project
                                </>
                              )}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
} 